
import {Component, Vue} from "vue-property-decorator";
import {PathToProduct, Product} from "@/enum/Product";
import {ProgramPaths} from "@/enum/ProgramPaths";
import {ENGEL_TAZELEME_ALT_SINIR_MS, LisansDurum, yenilemeEtiketi, yenilemeGerekliMi, yenilemeHedefi} from "@/utils/lisansKisit";
import {URUN_KATALOGU, urunBilgisi} from "@/utils/urunKatalogu";

/**
 * DEV-2113 — lisans kaynakli engelde gosterilen ekran.
 *
 * NEDEN VAR: onceden kullanici yalnizca "403 Bu Sayfa Icin Yetkiniz Yok" yazan
 * bos bir sayfaya dusuyordu. Lisansin bittigi, ne zaman bittigi ve nasil
 * yenilenecegi hicbir yerde yazmiyordu; talebin yeniden acilma gerekcelerinden
 * biri buydu ("ekranlar kotu ve yonlendirmiyor").
 *
 * Ekran yalnizca GOSTERIR; engelleme sunucuda yapilir. Urun adi, rengi ve
 * simgesi urunKatalogu'ndan gelir (lisans uyarisi da ayni kaynagi kullanir).
 */
@Component
export default class LisansDurumuView extends Vue {
  /** Yenileme istegi giden urun (buton yuklenme durumu). */
  yukleniyor: number | null = null;

  /** Ekranin cekirdegi: uc ana urun daima listelenir. */
  private static readonly CEKIRDEK: Product[] = [Product.OFISPRO, Product.ICRAPROWEB, Product.EICRAPRO];

  /** Kullanicinin girmeye calistigi urun (router query ile tasiyor). */
  get gelinenUrun(): Product | null {
    const yol = String(this.$route.query.urun ?? "").replace(/^\//, "");
    if (!yol) return null;
    // @ts-ignore -- PathToProduct string anahtarli enum
    const urun = PathToProduct[yol];
    return typeof urun === "number" ? urun : null;
  }

  get gelinenUrunAdi(): string | null {
    return urunBilgisi(this.gelinenUrun)?.ad ?? null;
  }

  /**
   * Cekirdek uc urun + (varsa) kullanicinin girmeye calistigi urun.
   *
   * ArabulucuPro/DavaPro her kullaniciya "Lisans yok" satiri olarak
   * gosterilmez -- satin alinmamis urun icin gurultu olurdu. Ama muhafiz
   * kullaniciyi O URUNDEN buraya attiysa satir gorunur; yoksa kullanici
   * neden engellendigini anlatan tek satiri goremezdi.
   */
  get urunler() {
    return URUN_KATALOGU.filter(
      (u) => LisansDurumuView.CEKIRDEK.includes(u.urun) || u.urun === this.gelinenUrun
    );
  }

  /**
   * Donulecek urun: gelinen urunun ekranlari aciksa (kisitli ya da lisans
   * yenilenmis) o urun, degilse ekranlarina girilebilen ILK urun; hic yoksa
   * null (buton cizilmez).
   */
  get donusHedefi(): {ad: string; yol: ProgramPaths; renk: string} | null {
    const acik = URUN_KATALOGU.filter((u) => this.$helper.lisansEkranlariAcikMi(u.urun));
    const hedef = acik.find((u) => u.urun === this.gelinenUrun) ?? acik[0];
    return hedef ? {ad: hedef.ad, yol: hedef.yol, renk: hedef.renk} : null;
  }

  private get gelinenUrunDurumu(): LisansDurum | null {
    const urun = this.gelinenUrun;
    return urun === null ? null : this.$helper.lisansDurumu(urun);
  }

  /**
   * Baslik duruma gore degisir. "Kullanim sureniz doldu" cumlesi, o urunu HIC
   * satin almamis kullaniciya yanlis bilgi verir -- dolan bir sure yok.
   */
  get baslik(): string {
    if (this.gelinenUrunDurumu === "yok") return "Bu ürün için lisansınız bulunmuyor";
    if (this.gelinenUrunDurumu === "kisitli") return "Kısıtlı moddasınız";
    // Ekran acikken lisans yenilenmis olabilir (magaza sekmesinden donus).
    if (this.gelinenUrunDurumu === "gecerli") return `${this.gelinenUrunAdi} lisansınız geçerli`;
    return "Kullanım süreniz doldu";
  }

  get altBaslik(): string {
    if (this.gelinenUrunDurumu === "yok") {
      return "Bu bölümü kullanabilmek için ilgili paketi satın almanız gerekiyor.";
    }
    if (this.gelinenUrunDurumu === "kisitli") {
      return "Kayıtlarınızı görüntüleyebilirsiniz; yeni işlem yapmak için süreyi uzatmanız gerekiyor.";
    }
    if (this.gelinenUrunDurumu === "gecerli") {
      return "Bu bölümü kullanmaya devam edebilirsiniz.";
    }
    return "Aşağıdaki ürünlerin kullanım süresi sona erdiği için bu bölüme girilemiyor.";
  }

  get baslikIkonu(): string {
    if (this.gelinenUrunDurumu === "yok") return "mdi-cart-outline";
    if (this.gelinenUrunDurumu === "kisitli") return "mdi-timer-sand";
    if (this.gelinenUrunDurumu === "gecerli") return "mdi-check-circle-outline";
    return "mdi-clock-alert-outline";
  }

  /** Baslik rozetinin tonu: satin alma bilgi (mavi), kisitli uyari, bitmis hata, gecerli basari. */
  get ton(): "bilgi" | "uyari" | "hata" | "basari" {
    if (this.gelinenUrunDurumu === "yok") return "bilgi";
    if (this.gelinenUrunDurumu === "kisitli") return "uyari";
    if (this.gelinenUrunDurumu === "gecerli") return "basari";
    return "hata";
  }

  get urunDurumlari() {
    const lisanslar = this.$store.getters.licenses;
    return this.urunler.map(({urun, ad, renk, simge}) => {
      const durum = this.$helper.lisansDurumu(urun) as LisansDurum;
      const lisans = lisanslar
        .slice()
        .reverse()
        .find((l: any) => l.product_id == urun);
      const tarih = lisans ? this.$helper.tarihStr(lisans.end_date) : null;

      return {
        urun,
        ad,
        renk,
        simge,
        durum,
        metin: this.durumMetni(durum),
        tarihMetni: this.tarihMetni(durum, tarih, this.$helper.lisansKisitliKalanGun(urun)),
        yenilenebilir: yenilemeGerekliMi(lisanslar, urun, this.$helper.lisansKisitGun()),
        eylem: yenilemeEtiketi(durum),
      };
    });
  }

  /** Kisitli donemdeki urunler icin "N gun sonra" uyarisi. */
  get kisitliUyarisi(): string | null {
    const kisitli = this.urunler
      .map(({urun, ad}) => ({ad, kalan: this.$helper.lisansKisitliKalanGun(urun)}))
      .filter((s) => s.kalan !== null);

    if (kisitli.length === 0) return null;

    return kisitli
      .map((s) => `${s.ad} kısıtlı modda: ${s.kalan} gün sonra kısıtlı mod sona erecek.`)
      .join(" ");
  }

  durumMetni(durum: string): string {
    if (durum === "gecerli") return "Geçerli";
    if (durum === "kisitli") return "Kısıtlı mod";
    if (durum === "sure_bitti") return "Süresi doldu";
    return "Lisans yok";
  }

  tarihMetni(durum: string, tarih: string | null, kalanGun: number | null): string {
    if (durum === "yok" || !tarih) return "Bu hesapta tanımlı değil";
    if (durum === "gecerli") return `Bitiş: ${tarih}`;
    if (durum === "kisitli" && kalanGun !== null) return `${tarih} tarihinde doldu · ${kalanGun} gün kısıtlı kullanım`;
    return `${tarih} tarihinde doldu`;
  }

  /**
   * Urun bazli yenileme. Icrapro ve e-Icrapro'da self-servis akis calisir
   * (tek kullanimlik otomatik giris + indirimli sepet); OfisPro'yu o uc
   * tanimadigi icin magaza baglantisi acilir.
   */
  async yenile(urun: number) {
    const hedef = yenilemeHedefi(this.$store.getters.licenses, urun);
    if (hedef.tur === "baglanti") {
      window.open(hedef.url, "_blank");
      return;
    }

    this.yukleniyor = urun;
    try {
      await this.$store.dispatch("startRenewalCheckout", hedef.product);
    } finally {
      this.yukleniyor = null;
    }
  }

  geriDon() {
    if (this.donusHedefi) this.$router.push(this.donusHedefi.yol);
  }

  /**
   * Ekran acilinca ve sekmeye geri donulunce (magazadan odeme sonrasi) ozet
   * tazelenir: lisans yenilendiyse ekran bunu hemen gostersin, kullanici eski
   * "suresi doldu" bilgisiyle kalmasin. En fazla 30 saniyede bir.
   */
  mounted() {
    this.tazele();
    document.addEventListener("visibilitychange", this.gorunurlukDegisti);
  }

  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.gorunurlukDegisti);
  }

  tazele() {
    this.$store.dispatch("ozellikDurumunuTazele", {enAzAralikMs: ENGEL_TAZELEME_ALT_SINIR_MS}).catch(() => undefined);
  }

  gorunurlukDegisti() {
    if (document.visibilityState === "visible") this.tazele();
  }
}
